import {
  ChevronsLeft,
  ChevronsRight,
  FileText,
  Inbox,
  Plus,
  Send,
  Settings2,
  ShieldAlert,
  Star,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { confirmModal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { MailBoxApi } from '../hooks/useMailBox'
import { GROUP_DOT_CLASS, type MailFolder, type MailNavView } from '../mailTypes'
import { GroupDropTarget } from './GroupDropTarget'

const COLLAPSE_KEY = 'cp.mail.nav.collapsed'

type Props = {
  box: MailBoxApi
  onCompose: () => void
  onOpenSettings: () => void
  /** Çekmece içinde: daraltma yok, bir görünüm seçilince çekmece kapanır. */
  inDrawer?: boolean
  onNavigate?: () => void
  className?: string
}

function isActive(view: MailNavView, target: MailNavView): boolean {
  if (view.kind !== target.kind) return false
  if (view.kind === 'folder' && target.kind === 'folder') return view.folder === target.folder
  if (view.kind === 'group' && target.kind === 'group') return view.groupId === target.groupId
  return view.kind === 'favorites' && target.kind === 'favorites'
}

function NavButton({
  active,
  onClick,
  icon: Icon,
  label,
  count,
  collapsed,
}: {
  active: boolean
  onClick: () => void
  icon: typeof Inbox
  label: string
  count?: number
  collapsed: boolean
}) {
  const btn = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center rounded-full text-sm transition-colors',
        collapsed ? 'size-10 justify-center' : 'w-full gap-3 px-3 py-2',
        active
          ? 'bg-primary/12 font-semibold text-primary'
          : 'font-medium text-foreground/80 hover:bg-muted',
      )}
      aria-label={label}
    >
      <Icon className="size-4 shrink-0 opacity-80" aria-hidden />
      {collapsed ? null : (
        <>
          <span className="min-w-0 flex-1 truncate text-left">{label}</span>
          {count != null && count > 0 ? (
            <span
              className={cn(
                'min-w-5 rounded-full px-1.5 text-center text-xs tabular-nums',
                active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
              )}
            >
              {count}
            </span>
          ) : null}
        </>
      )}
    </button>
  )
  return collapsed ? <Tooltip content={label}>{btn}</Tooltip> : btn
}

export function MailSidebar({ box, onCompose, onOpenSettings, inDrawer, onNavigate, className }: Props) {
  const { t } = useTranslation()
  const [newGroup, setNewGroup] = useState('')
  const [storedCollapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1'
    } catch {
      return false
    }
  })

  const collapsed = !inDrawer && storedCollapsed
  const go = (v: MailNavView) => {
    box.selectView(v)
    onNavigate?.()
  }

  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        /* yok say */
      }
      return next
    })
  }

  const folders: { folder: MailFolder; icon: typeof Inbox; label: string; count?: number }[] = [
    { folder: 'INBOX', icon: Inbox, label: t('mail.mailbox.folders.inbox'), count: box.counts.inboxUnread },
    { folder: 'SENT', icon: Send, label: t('mail.mailbox.folders.sent'), count: box.counts.sent },
    { folder: 'DRAFT', icon: FileText, label: t('mail.mailbox.folders.drafts'), count: box.counts.drafts },
    { folder: 'SPAM', icon: ShieldAlert, label: t('mail.mailbox.folders.spam'), count: box.counts.spam },
    { folder: 'TRASH', icon: Trash2, label: t('mail.mailbox.folders.trash'), count: box.counts.trash },
  ]

  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col bg-muted/30 transition-[width]',
        inDrawer ? 'w-full' : cn('border-r border-border', collapsed ? 'w-14' : 'w-56'),
        className,
      )}
    >
      <div className={cn('flex items-center gap-1', collapsed ? 'flex-col px-1.5 pt-3' : 'px-3 pb-2 pt-3')}>
        {collapsed ? (
          <Tooltip content={t('mail.mailbox.compose')}>
            <Button className="size-10 rounded-xl shadow-sm" size="icon" onClick={() => { onCompose(); onNavigate?.() }} aria-label={t('mail.mailbox.compose')}>
              <Plus className="size-4" />
            </Button>
          </Tooltip>
        ) : (
          <Button className="h-10 flex-1 rounded-xl shadow-sm" onClick={() => { onCompose(); onNavigate?.() }}>
            <Plus className="size-4" />
            {t('mail.mailbox.compose')}
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('size-9 shrink-0', inDrawer && 'hidden')}
          aria-label={collapsed ? t('mail.mailbox.expandNav') : t('mail.mailbox.collapseNav')}
          onClick={toggleCollapsed}
        >
          {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
        </Button>
      </div>

      <nav
        className={cn('flex-1 space-y-4 overflow-y-auto scrollbar-none pb-2', collapsed ? 'px-1.5' : 'px-2')}
        aria-label={t('mail.mailbox.nav')}
      >
        <div className={cn('space-y-0.5', collapsed && 'flex flex-col items-center')}>
          {folders.slice(0, 1).map((f) => (
            <NavButton
              key={f.folder}
              collapsed={collapsed}
              active={isActive(box.view, { kind: 'folder', folder: f.folder })}
              onClick={() => go({ kind: 'folder', folder: f.folder })}
              icon={f.icon}
              label={f.label}
              count={f.count}
            />
          ))}
          <NavButton
            collapsed={collapsed}
            active={isActive(box.view, { kind: 'favorites' })}
            onClick={() => go({ kind: 'favorites' })}
            icon={Star}
            label={t('mail.mailbox.folders.favorites')}
            count={box.counts.favorites}
          />
          {folders.slice(1).map((f) => (
            <NavButton
              key={f.folder}
              collapsed={collapsed}
              active={isActive(box.view, { kind: 'folder', folder: f.folder })}
              onClick={() => go({ kind: 'folder', folder: f.folder })}
              icon={f.icon}
              label={f.label}
              count={f.count}
            />
          ))}
        </div>

        {!collapsed ? (
          <div className="space-y-2">
            <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('mail.mailbox.groups.title')}
            </p>
            <div className="space-y-0.5">
              {box.groups.map((g) => (
                <div key={g.id} className="group flex items-center gap-0.5">
                  <GroupDropTarget groupId={g.id} className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => go({ kind: 'group', groupId: g.id })}
                      className={cn(
                        'flex w-full min-w-0 items-center gap-3 rounded-full px-3 py-2 text-sm transition-colors',
                        isActive(box.view, { kind: 'group', groupId: g.id })
                          ? 'bg-primary/12 font-semibold text-primary'
                          : 'font-medium text-foreground/80 hover:bg-muted',
                      )}
                    >
                      <span className={cn('size-2.5 shrink-0 rounded-full', GROUP_DOT_CLASS[g.colorToken])} />
                      <span className="min-w-0 flex-1 truncate text-left">{g.name}</span>
                      {(box.counts.byGroup[g.id] ?? 0) > 0 ? (
                        <span className="text-xs tabular-nums text-muted-foreground">{box.counts.byGroup[g.id]}</span>
                      ) : null}
                    </button>
                  </GroupDropTarget>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 opacity-0 group-hover:opacity-100"
                    aria-label={t('mail.mailbox.groups.delete')}
                    onClick={() =>
                      void confirmModal({
                        title: t('confirm.mailGroupDelete.title'),
                        description: t('confirm.mailGroupDelete.body', { name: g.name }),
                        confirmLabel: t('mail.mailbox.groups.delete'),
                        destructive: true,
                      }).then((ok) => {
                        if (ok) return box.deleteGroup(g.id)
                      })
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <form
              className="flex gap-1.5 px-1.5"
              onSubmit={(e) => {
                e.preventDefault()
                box.addGroup(newGroup)
                setNewGroup('')
              }}
            >
              <Input
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
                placeholder={t('mail.mailbox.groups.newPlaceholder')}
                className="h-8 rounded-full bg-card text-xs"
                aria-label={t('mail.mailbox.groups.newPlaceholder')}
              />
              <Button type="submit" size="icon" variant="outline" className="size-8 shrink-0 rounded-full" aria-label={t('mail.mailbox.groups.add')}>
                <Plus className="size-3.5" />
              </Button>
            </form>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-0.5">
            {box.groups.map((g) => (
              <GroupDropTarget key={g.id} groupId={g.id}>
                <Tooltip content={g.name}>
                  <button
                    type="button"
                    onClick={() => go({ kind: 'group', groupId: g.id })}
                    className={cn(
                      'flex size-10 items-center justify-center rounded-full transition-colors',
                      isActive(box.view, { kind: 'group', groupId: g.id }) ? 'bg-primary/12' : 'hover:bg-muted',
                    )}
                    aria-label={g.name}
                  >
                    <span className={cn('size-2.5 rounded-full', GROUP_DOT_CLASS[g.colorToken])} />
                  </button>
                </Tooltip>
              </GroupDropTarget>
            ))}
          </div>
        )}
      </nav>

      <div className={cn('border-t border-border', collapsed ? 'flex justify-center p-1.5' : 'px-2 py-2')}>
        {collapsed ? (
          <Tooltip content={t('mail.mailbox.settings.title')}>
            <Button variant="ghost" size="icon" className="size-10 rounded-full" onClick={onOpenSettings} aria-label={t('mail.mailbox.settings.title')}>
              <Settings2 />
            </Button>
          </Tooltip>
        ) : (
          <Button variant="ghost" size="sm" className="h-9 w-full justify-start rounded-full px-3" onClick={onOpenSettings}>
            <Settings2 />
            {t('mail.mailbox.settings.title')}
          </Button>
        )}
      </div>
    </aside>
  )
}
